import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import { ThemeOverlay } from "./ThemeOverlay";
import { resolveCardTheme } from "./theme";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type DailyChallengeCardContent,
} from "./types";

interface DailyChallengeCardProps {
  format: CardFormat;
  content: DailyChallengeCardContent;
}

const DailyChallengeCard = forwardRef<
  HTMLDivElement,
  DailyChallengeCardProps
>(function DailyChallengeCard({ format, content }, ref) {
  const isStory = format === "story";
  const style = content.style ?? {};
  const theme = resolveCardTheme(content.theme ?? "signature", {
    monoColor: content.monoColor,
  });

  const eyebrowStyle: CSSProperties = {
    ...resolveTextStyle(
      style.eyebrow,
      {
        fontFamily: theme.fontEyebrow,
        fontSizePx: 24,
        color: theme.accentColor,
        letterSpacing: "0.4em",
        bold: true,
      },
      format,
    ),
    textTransform: "uppercase",
    marginTop: isStory ? 80 : 56,
  };

  const chipFontSize = isStory ? 22 : 18;
  const chipStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    padding: isStory ? "12px 24px" : "10px 20px",
    borderRadius: 9999,
    background: `${theme.accentColor}26`, // ~15% opacity
    border: `1px solid ${theme.accentColor}8c`, // ~55%
    color: theme.accentColor,
    fontSize: chipFontSize,
    fontWeight: 800,
    letterSpacing: "0.3em",
    textTransform: "uppercase",
    marginTop: isStory ? 28 : 18,
    alignSelf: "flex-start",
  };

  const headlineStyle: CSSProperties = {
    ...resolveTextStyle(
      style.headline,
      {
        fontFamily: theme.fontHeadline,
        fontSizePx: 124,
        color: theme.headlineColor,
        letterSpacing: "-0.025em",
        bold: true,
      },
      format,
    ),
    lineHeight: 0.95,
    marginTop: isStory ? 32 : 22,
  };

  const goldStyle: CSSProperties = {
    ...resolveTextStyle(
      style.goldHighlight,
      {
        fontFamily: theme.fontHeadline,
        fontSizePx: 124,
        color: theme.accentColor,
        bold: true,
      },
      format,
    ),
    display: "block",
    lineHeight: 0.95,
    fontSize: (style.headline?.fontSizePx ?? style.goldHighlight?.fontSizePx ?? 124) * (isStory ? 1 : 0.78),
  };

  const subtextStyle: CSSProperties = {
    ...resolveTextStyle(
      style.body,
      {
        fontFamily: theme.fontBody,
        fontSizePx: 30,
        color: theme.subtleTextColor,
        bold: false,
      },
      format,
    ),
    lineHeight: 1.4,
    marginTop: isStory ? 36 : 26,
    maxWidth: isStory ? 880 : 880,
  };

  const ctaFontSize = isStory ? 28 : 24;
  const ctaStyle: CSSProperties = {
    ...resolveTextStyle(
      style.ctaText,
      {
        fontFamily: theme.fontBody,
        fontSizePx: 28,
        color: theme.ctaFg,
        bold: true,
      },
      format,
    ),
    display: "inline-flex",
    alignItems: "center",
    gap: 12,
    marginTop: isStory ? 56 : 36,
    padding: isStory ? "22px 40px" : "18px 32px",
    borderRadius: 9999,
    background: theme.ctaBg,
    fontSize: ctaFontSize,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    boxShadow: `0 20px 50px ${theme.ctaBg}40`,
    alignSelf: "flex-start",
    textAlign: undefined,
  };

  return (
    <FbkCardShell
      ref={ref}
      format={format}
      footerLeft={content.footerLeft || defaultFooterLeft("DAILY_CHALLENGE")}
      footerRight={content.hashtags || "#WC2026 · #FBK · #DAILYCHALLENGE"}
      header={content.header}
      background={style.background}
      customBaseGradient={theme.customBaseGradient}
      headerOnLight={theme.headerOnLight}
      suppressBrandOverlays={theme.suppressBrandOverlays}
      hideFooter={content.footer?.hide}
    >
      <ThemeOverlay theme={theme} format={format} />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div style={eyebrowStyle}>DAILY CHALLENGE</div>
        <div style={chipStyle}>{content.challengeType}</div>

        <div style={headlineStyle}>
          {content.headline}
          {content.goldHighlight && (
            <span style={goldStyle}>{content.goldHighlight}</span>
          )}
        </div>

        {content.subtext && <div style={subtextStyle}>{content.subtext}</div>}

        {content.ctaText && <div style={ctaStyle}>{content.ctaText} →</div>}
      </div>
    </FbkCardShell>
  );
});

export default DailyChallengeCard;
